import React, {FC, Fragment, ReactNode, RefObject, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {CheckBox} from '../../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {boxedInputColorReaders} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {DropdownBoxCoordinator} from '../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dropdown, DropdownProps} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {ChannelIconSizesEnum} from '../../../../../../libs/shared/ui/src/components/icon/channelIcon';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {
  CellsHeaderFiltersProps,
  renderFilterIcon,
  renderFilterName,
} from '../../../cells/header/cellsHeaderFilters';
import {renderSearchInTitle} from '../../hints/searchHintIn/searchHintIn';
import {SearchHintInDropdown} from '../../hints/searchHintIn/searchHintInDropdown';
import {renderStatusModifierTitle} from '../../hints/searchHintIs/searchModifierStatus';
import {SearchModifierStatusDropdown} from '../../hints/searchHintIs/searchModifierStatusDropdown';
import {useSearchCardsFromQuery} from '../../hints/searchModifiersHooks';
import {InboxesSearchLocationDropdown} from '../filters/inboxesSearchFilterDropdown';
import {BuiltQuery} from './builtQuery';
import {ConversationCustomFieldField} from './fields/conversationCustomField';
import {
  MultiSelectMessageTypesDropdown,
  renderMessageTypeIcon,
  renderMessageTypePillLabel,
  SearchableMessageTypesEnum,
} from './fields/multiSelectChannelTypesDropdown';
import {MultiSelectContactField} from './fields/multiSelectContactField';
import {MultiSelectTagsDropdown} from './fields/multiSelectTagsDropdown';
import {MultiSelectTeammateField} from './fields/multiSelectTeammateField';
import {SearchDateField} from './fields/searchDateField';
import {SingleSelectAccountField} from './fields/singleSelectAccountField';
import {SingleSelectTeammateField} from './fields/singleSelectTeammateField';
import {useStatusDropdownItems} from './inboxesSearchQueryBuilderHelpers';
import {InboxesSearchQueryBuilderTextInput} from './inboxesSearchQueryBuilderTextInput';
import {InboxesSearchQueryBuilderInputsEnum} from './inboxesSearchQueryBuilderTypes';

/*
 * Props.
 */

interface InboxesSearchQueryBuilderDropdownProps extends DropdownProps {
  innerRef: RefObject<HTMLDivElement>;
  searchQuery?: string;
  queryHash?: string;

  onSearchClick: () => void;
  onResetClick: () => void;

  builtQuery: BuiltQuery;
  setBuiltQuery: (query: BuiltQuery) => void;

  searchFilters: CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
  selectedLocation: InboxesSearchLocationsEnum;
  onSearchLocationSelect: (arg: InboxesSearchLocationsEnum) => void;
}

/*
 * Styles.
 */

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 0px;
  min-height: 0;
`;

const StyledFormDiv = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  grid-column-gap: 8px;
  grid-row-gap: 16px;
  grid-auto-rows: max-content;

  min-height: 0px;
  max-height: 640px;
  overflow-y: scroll;
  padding: 0px 16px;

  align-items: center;
  justify-items: start;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  flex-shrink: 0;
  padding: 20px 20px 0px;
`;

const StyledLabelSpan = styled.span`
  display: flex;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
  gap: 4px;
`;

// When the custom field field wraps on a second row, this makes the label centered on the first row.
const StyledCustomFieldLabel = styled(StyledLabelSpan)`
  height: 30px;
  align-items: center;
  align-self: start;
`;

const StyledPlaceholderSpan = styled.span`
  color: ${boxedInputColorReaders.textPlaceholder};
`;

const StyledFlexDiv = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterIconDiv = styled.div`
  color: ${(p) => p.theme.greys.shade90};
  ${center()};
  align-items: end;
  width: 24px;
`;

const Grey60Div = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledStretchedGridRowDiv = styled.div`
  justify-self: stretch;
`;

/*
 * Component.
 */

export const InboxesSearchQueryBuilderDropdown: FC<InboxesSearchQueryBuilderDropdownProps> = (props) => {
  const {
    innerRef,
    searchQuery,
    queryHash,
    onSearchClick,
    builtQuery,
    setBuiltQuery,
    searchFilters,
    selectedLocation,
    onSearchLocationSelect,
    onResetClick,
    ...dropdownProps
  } = props;

  const helpCenterUrl = 'https://help.front.com/en/articles/2131';

  const localize = useLocalize();
  const {navigateToLink} = useLinkNavigation();
  const theme = useUserTheme();

  const [isExpanded, setIsExpanded] = useState(false);
  const fromContacts = useSearchCardsFromQuery(builtQuery.render(), ModifiersEnum.FROM);
  const toContacts = useSearchCardsFromQuery(builtQuery.render(), ModifiersEnum.TO);
  const withContacts = useSearchCardsFromQuery(builtQuery.render(), ModifiersEnum.WITH);

  const renderAssigneeInput = () => {
    // Use the first value, there shouldn't be more than one value.
    const onlySelectedValue = builtQuery.getModifierValues(ModifiersEnum.ASSIGNEE).at(0);

    return (
      <SingleSelectTeammateField
        dropdownTitle={<Trans id="search-query-builder-assignee-dropdown-title">Assignee</Trans>}
        onChange={(value: TeammateModel): void => {
          setBuiltQuery(builtQuery.withModifierValues(ModifiersEnum.ASSIGNEE, [value.alias]));
        }}
        onDelete={() => {
          setBuiltQuery(builtQuery.withEmptyModifierValues(ModifiersEnum.ASSIGNEE));
        }}
        selectedTeammateAlias={onlySelectedValue}
      />
    );
  };

  const renderMultiTeammateInput = (
    modifier:
      | ModifiersEnum.MENTION
      | ModifiersEnum.AUTHOR
      | ModifiersEnum.MENTION
      | ModifiersEnum.PARTICIPANT,
    dropdownTitle: ReactNode,
  ) => {
    const selectedValues = builtQuery.getModifierValues(modifier);
    return (
      <MultiSelectTeammateField
        initiallySelectedAliases={new Set(selectedValues)}
        onAliasesSelectionChange={(selectedAliases: ReadonlySet<string>) => {
          setBuiltQuery(builtQuery.withModifierValues(modifier, selectedAliases));
        }}
        dropdownTitle={dropdownTitle}
      />
    );
  };

  const {statuses, shouldRenderSeparator} = useStatusDropdownItems({
    selectedLocation,
    parameters: searchFilters.parameters,
  });

  const renderStatusInput = () => {
    const selectedValues = builtQuery.getModifierValues(ModifiersEnum.IS);
    const hasSelectedValue = selectedValues.length > 0;

    return (
      <DropdownBoxCoordinator
        shouldDisablePadding
        leftIcon="messageSmall"
        leftIconColor={hasSelectedValue ? theme.greys.shade90 : undefined}
        value={
          hasSelectedValue ? (
            renderStatusModifierTitle(statuses, selectedValues, localize)
          ) : (
            <StyledPlaceholderSpan>
              <Trans id="search-query-builder-status-placeholder">Select status</Trans>
            </StyledPlaceholderSpan>
          )
        }
        renderDropdown={(popoverProps) => (
          <SearchModifierStatusDropdown
            popoverProps={popoverProps}
            items={statuses}
            selectedItems={selectedValues}
            onClick={(value) => {
              setBuiltQuery(builtQuery.withToggledModifierValue(ModifiersEnum.IS, value));
            }}
            onClear={() => {
              setBuiltQuery(builtQuery.withEmptyModifierValues(ModifiersEnum.IS));
            }}
            shouldRenderSeparator={shouldRenderSeparator}
          />
        )}
      />
    );
  };

  const renderScopeInput = () => {
    const selectedValues = builtQuery.getModifierValues(ModifiersEnum.IN);
    const hasSelectedValue = selectedValues.length > 0;

    return (
      <DropdownBoxCoordinator
        shouldDisablePadding
        leftIcon="discussionSmall"
        leftIconColor={hasSelectedValue ? theme.greys.shade90 : undefined}
        value={
          hasSelectedValue ? (
            renderSearchInTitle(selectedValues, localize)
          ) : (
            <StyledPlaceholderSpan>
              <Trans id="search-query-builder-in-placeholder">Select type</Trans>
            </StyledPlaceholderSpan>
          )
        }
        renderDropdown={(popoverProps) => (
          <SearchHintInDropdown
            popoverProps={popoverProps}
            selectedValues={selectedValues}
            onClick={(_, value) => {
              setBuiltQuery(builtQuery.withToggledModifierValue(ModifiersEnum.IN, value));
            }}
            onClear={() => {
              setBuiltQuery(builtQuery.withEmptyModifierValues(ModifiersEnum.IN));
            }}
          />
        )}
      />
    );
  };

  const renderChannelTypeInput = () => {
    // FIXME(ldemarne): Remove cast.
    const selectedValues = builtQuery.getModifierValues(
      ModifiersEnum.CHANNEL_TYPE,
    ) as ReadonlyArray<SearchableMessageTypesEnum>;
    const firstSelectedValue = selectedValues.at(0);
    const hasSelectedValue = isDefined(firstSelectedValue);

    return (
      <DropdownBoxCoordinator
        shouldDisablePadding
        noInputMarginLeft
        value={
          <StyledFlexDiv>
            <FilterIconDiv>
              {hasSelectedValue ? (
                renderMessageTypeIcon(firstSelectedValue, ChannelIconSizesEnum.SMALL, 16)
              ) : (
                <Grey60Div>
                  <Icon name="channelsSmall" />
                </Grey60Div>
              )}
            </FilterIconDiv>
            {renderMessageTypePillLabel(
              selectedValues,
              <StyledPlaceholderSpan>
                <Trans id="search-query-builder-status-placeholder">Select channel type</Trans>
              </StyledPlaceholderSpan>,
              localize,
            )}
          </StyledFlexDiv>
        }
        renderDropdown={(popoverProps) => (
          <MultiSelectMessageTypesDropdown
            initialSelection={new Set(selectedValues)}
            onSelectionChange={(selection: ReadonlySet<string>) => {
              setBuiltQuery(builtQuery.withModifierValues(ModifiersEnum.CHANNEL_TYPE, selection));
            }}
            popoverProps={popoverProps}
          />
        )}
      />
    );
  };

  const renderBaseFilters = () => [
    <Fragment key="location">
      <StyledLabelSpan>
        <Trans id="search-query-builder-label-location">Location</Trans>
      </StyledLabelSpan>
      <LocationField
        searchFilters={searchFilters}
        selectedLocation={selectedLocation}
        onSearchLocationSelect={onSearchLocationSelect}
      />
    </Fragment>,

    <Fragment key="from">
      <StyledLabelSpan>
        <Trans id="search-query-builder-from">From</Trans>
        <TooltipCoordinator
          render={() => (
            <Trans id="search-query-builder-tooltip-from">
              Search for conversations sent by a specific email address, contact or teammate. Search results
              will include any of the selected senders.
            </Trans>
          )}
        />
      </StyledLabelSpan>
      <MultiSelectContactField
        modifier={ModifiersEnum.FROM}
        selectedContacts={fromContacts}
        onChange={(selectedContacts) => {
          setBuiltQuery(builtQuery.withModifierValues(ModifiersEnum.FROM, selectedContacts));
        }}
        onClear={() => {
          setBuiltQuery(builtQuery.withEmptyModifierValues(ModifiersEnum.FROM));
        }}
      />
    </Fragment>,
    <Fragment key="to">
      <StyledLabelSpan>
        <Trans id="search-query-builder-to">To</Trans>

        <TooltipCoordinator
          render={() => (
            <Trans id="search-query-builder-tooltip-to">
              Search for emails, contacts or teammates in To, CC, BCC fields. Search results will include any
              of the selected recipients.
            </Trans>
          )}
        />
      </StyledLabelSpan>
      <MultiSelectContactField
        modifier={ModifiersEnum.TO}
        selectedContacts={toContacts}
        onChange={(selectedContacts) => {
          setBuiltQuery(builtQuery.withModifierValues(ModifiersEnum.TO, selectedContacts));
        }}
        onClear={() => {
          setBuiltQuery(builtQuery.withEmptyModifierValues(ModifiersEnum.TO));
        }}
      />
    </Fragment>,
    <Fragment key="subject">
      <StyledLabelSpan>
        <Trans id="search-query-builder-label-subject">Subject</Trans>
      </StyledLabelSpan>
      <StyledStretchedGridRowDiv>
        <InboxesSearchQueryBuilderTextInput
          id={InboxesSearchQueryBuilderInputsEnum.SUBJECT}
          inputValue={{value: builtQuery.getFreeformModifierValue(ModifiersEnum.SUBJECT)}}
          onChange={({value}) => {
            setBuiltQuery(builtQuery.withFreeformModifierValue(ModifiersEnum.SUBJECT, value));
          }}
          onSearch={onSearchClick}
          icon="contentSmall"
          placeholder="Add keywords (ex: “invoice; contract”)"
        />
      </StyledStretchedGridRowDiv>
    </Fragment>,
    <Fragment key="body">
      <StyledLabelSpan>
        <Trans id="search-query-builder-label-body">Message body</Trans>
      </StyledLabelSpan>
      <StyledStretchedGridRowDiv>
        <InboxesSearchQueryBuilderTextInput
          id={InboxesSearchQueryBuilderInputsEnum.BODY_CONTAINS}
          inputValue={{value: builtQuery.getFreeformModifierValue(ModifiersEnum.BODY)}}
          onChange={({value}) => {
            setBuiltQuery(builtQuery.withFreeformModifierValue(ModifiersEnum.BODY, value));
          }}
          onSearch={onSearchClick}
          icon="contentSmall"
          placeholder="Add keywords (ex: “follow up; order 123”)"
        />
      </StyledStretchedGridRowDiv>
    </Fragment>,
    <Fragment key="date">
      <StyledLabelSpan>
        <Trans id="search-query-builder-date">Date</Trans>
      </StyledLabelSpan>
      <SearchDateField
        selectedDateRange={builtQuery.getSelectedDateRange()}
        setDatePeriod={(selectedDateRange) => {
          setBuiltQuery(builtQuery.withSelectedDateRange(selectedDateRange));
        }}
        clearDatePeriod={() => {
          setBuiltQuery(builtQuery.withSelectedDateRange(undefined));
        }}
      />
    </Fragment>,
    <Fragment key="status">
      <StyledLabelSpan>
        <Trans id="search-query-builder-status">Conversation status</Trans>
      </StyledLabelSpan>
      {renderStatusInput()}
    </Fragment>,
    <Fragment key="in">
      <StyledLabelSpan>
        <Trans id="search-query-builder-in">In</Trans>
        <TooltipCoordinator
          render={() => (
            <Trans id="search-query-builder-tooltip-in">
              Search inside a specific type of conversations. Search results will include any of the selected
              types.
            </Trans>
          )}
        />
      </StyledLabelSpan>
      {renderScopeInput()}
    </Fragment>,
    <Fragment key="has-attachment">
      <CheckBox
        isChecked={builtQuery.hasModifierValue(ModifiersEnum.HAS, 'attachments')}
        onChange={(value: boolean) => {
          setBuiltQuery(builtQuery.withModifierValueSetTo(ModifiersEnum.HAS, 'attachments', value));
        }}
      >
        <StyledLabelSpan>
          <Trans id="search-query-builder-has-attachment">Has attachment</Trans>
        </StyledLabelSpan>
      </CheckBox>
      <div />
    </Fragment>,
    <Fragment key="exclude-calendar-invites">
      <CheckBox
        isChecked={builtQuery.hasModifierValue(ModifiersEnum.HAS, 'no-calendar')}
        onChange={(value: boolean) => {
          setBuiltQuery(builtQuery.withModifierValueSetTo(ModifiersEnum.HAS, 'no-calendar', value));
        }}
      >
        <StyledLabelSpan>
          <Trans id="search-query-builder-exclude-calendar-invites">Exclude calendar invites</Trans>
        </StyledLabelSpan>
      </CheckBox>
      <div />
    </Fragment>,
  ];

  const renderExtraFilters = () => [
    <Fragment key="tags">
      <StyledLabelSpan>
        <Trans id="search-query-builder-tags">Tags</Trans>
      </StyledLabelSpan>

      <MultiSelectTagsDropdown
        builtQuery={builtQuery}
        onSelectedTagsChange={(newSelectedTags) => {
          setBuiltQuery(
            builtQuery.withModifierValues(
              ModifiersEnum.TAG,
              newSelectedTags.map((e) => e.alias),
            ),
          );
        }}
      />
    </Fragment>,
    <Fragment key="assignee">
      <StyledLabelSpan>
        <Trans id="search-query-builder-assignee">Assignee</Trans>
      </StyledLabelSpan>
      {renderAssigneeInput()}
    </Fragment>,
    <Fragment key="mention">
      <StyledLabelSpan>
        <Trans id="search-query-builder-mention">Mention</Trans>
        <TooltipCoordinator
          render={() => (
            <Trans id="search-query-builder-tooltip-mention">
              Search conversations where a specific teammate was @ mentioned in comments.
            </Trans>
          )}
        />
      </StyledLabelSpan>
      {renderMultiTeammateInput(
        ModifiersEnum.MENTION,
        <Trans id="search-query-builder-mention">Mention</Trans>,
      )}
    </Fragment>,
    <Fragment key="teammate">
      <StyledLabelSpan>
        <Trans id="search-query-builder-with">With teammate</Trans>
        <TooltipCoordinator
          render={() => (
            <Trans id="search-query-builder-tooltip-in-with">
              Search for conversations involving a specific contact or teammate. Includes recipients and
              senders (From, To, CC and BBC fields), assignees, subscribers, commenters, and mentions.
            </Trans>
          )}
        />
      </StyledLabelSpan>
      <MultiSelectContactField
        modifier={ModifiersEnum.WITH}
        selectedContacts={withContacts}
        onChange={(selectedContacts) => {
          setBuiltQuery(builtQuery.withModifierValues(ModifiersEnum.WITH, selectedContacts));
        }}
        onClear={() => {
          setBuiltQuery(builtQuery.withEmptyModifierValues(ModifiersEnum.WITH));
        }}
      />
    </Fragment>,
    <Fragment key="author">
      <StyledLabelSpan>
        <Trans id="search-query-builder-author">Author</Trans>
        <TooltipCoordinator
          render={() => (
            <Trans id="search-query-builder-tooltip-author">
              Search for messages and comments sent by one of your teammates.
            </Trans>
          )}
        />
      </StyledLabelSpan>
      {renderMultiTeammateInput(ModifiersEnum.AUTHOR, <Trans id="search-query-builder-author">Author</Trans>)}
    </Fragment>,
    <Fragment key="participant">
      <StyledLabelSpan>
        <Trans id="search-query-builder-participant">Participant</Trans>
        <TooltipCoordinator
          render={() => (
            <Trans id="search-query-builder-tooltip-participant">
              Search for conversations involving a specific teammate. Includes only conversations where the
              teammate is a subscriber.
            </Trans>
          )}
        />
      </StyledLabelSpan>
      {renderMultiTeammateInput(
        ModifiersEnum.PARTICIPANT,
        <Trans id="search-query-builder-label-participant">Participant</Trans>,
      )}
    </Fragment>,
    <Fragment key="account">
      <StyledLabelSpan>
        <Trans id="search-query-builder-label-account">Account</Trans>
      </StyledLabelSpan>
      <SingleSelectAccountField
        selectedAccountAlias={builtQuery.getModifierValues(ModifiersEnum.ACCOUNT).at(0)}
        onAccountAliasSelection={(value) => {
          if (value) {
            setBuiltQuery(builtQuery.withModifierValues(ModifiersEnum.ACCOUNT, [value]));
          } else {
            setBuiltQuery(builtQuery.withEmptyModifierValues(ModifiersEnum.ACCOUNT));
          }
        }}
      />
    </Fragment>,
    <Fragment key="channel-type">
      <StyledLabelSpan>
        <Trans id="search-query-builder-label-channeltype">Channel type</Trans>
      </StyledLabelSpan>
      {renderChannelTypeInput()}
    </Fragment>,
    <Fragment key="customfield">
      <StyledCustomFieldLabel>
        <Trans id="search-query-builder-custom-field-label">Conversation custom field</Trans>
      </StyledCustomFieldLabel>
      <ConversationCustomFieldField
        selectedCustomFieldName={builtQuery.getCustomFieldName()}
        selectedCustomFieldValue={builtQuery.getCustomFieldValue()}
        selectedCustomFields={builtQuery.getCustomFieldFilters()}
        onChange={(selectedCustomField) => {
          setBuiltQuery(builtQuery.withCustomFieldFilter(selectedCustomField));
        }}
        onChangeMultiple={(selectedCustomFields) => {
          setBuiltQuery(builtQuery.withCustomFieldFilters(selectedCustomFields));
        }}
      />
    </Fragment>,
  ];

  return (
    <Dropdown {...dropdownProps}>
      <StyledContainer
        role="presentation"
        ref={innerRef}
        onDoubleClick={(e) => e.preventDefault()}
        tabIndex={-1}
      >
        <StyledFormDiv>
          {renderBaseFilters()}
          {isExpanded && renderExtraFilters()}
          <MoreFiltersToggle
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            numberOfExtraFiltersWithValues={countExtraFiltersWithValues(builtQuery)}
          />
        </StyledFormDiv>
        <StyledButtonGroup>
          <Button
            interactionId="search-query-builder-learn-how-to-search"
            rightIcon="externalLinkSmall"
            onClick={() => {
              navigateToLink(helpCenterUrl);
            }}
          >
            <Trans id="search-query-builder-learn-to-search">Learn how to search</Trans>
          </Button>
          <ButtonGroupSpacer />
          <Button onClick={onResetClick} interactionId="search-query-builder-cancel-button">
            <Trans id="search-query-builder-cancel">Cancel</Trans>
          </Button>
          <Button onClick={onSearchClick} variant={ButtonVariantsEnum.PRIMARY}>
            <Trans id="search-query-builder-search">Search</Trans>
          </Button>
        </StyledButtonGroup>
      </StyledContainer>
    </Dropdown>
  );
};

/*
 * Sub-components.
 */

interface MoreFiltersToggleProps {
  isExpanded: boolean;
  setIsExpanded: (val: boolean) => void;
  numberOfExtraFiltersWithValues: number;
}

const StyledOffsetFlexDiv = styled(StyledFlexDiv)`
  // We want the button text to be aligned with the field labels, not the hover border.
  position: relative;
  left: -14px;
`;

const MoreFiltersToggle: FC<MoreFiltersToggleProps> = (props) => {
  const {isExpanded, setIsExpanded, numberOfExtraFiltersWithValues} = props;

  if (isExpanded) {
    return (
      <StyledOffsetFlexDiv>
        <Button
          interactionId="search-query-builder-less-filters"
          variant={ButtonVariantsEnum.LINK}
          rightIcon="chevronUpSmall"
          onClick={() => {
            setIsExpanded(false);
          }}
        >
          <Trans id="search-query-builder-less-filters">Less filters</Trans>
        </Button>
      </StyledOffsetFlexDiv>
    );
  }

  return (
    <StyledOffsetFlexDiv>
      <Button
        interactionId="search-query-builder-more-filters"
        variant={ButtonVariantsEnum.LINK}
        rightIcon="chevronDownSmall"
        onClick={() => {
          setIsExpanded(true);
        }}
      >
        <Trans id="search-query-builder-more-filters">More filters</Trans>{' '}
        {numberOfExtraFiltersWithValues > 0 && ` (${numberOfExtraFiltersWithValues})`}
      </Button>
    </StyledOffsetFlexDiv>
  );
};

interface LocationFieldProps {
  searchFilters: CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
  selectedLocation: InboxesSearchLocationsEnum;
  onSearchLocationSelect: (arg: InboxesSearchLocationsEnum) => void;
}

const LocationField: FC<LocationFieldProps> = (props) => {
  const {searchFilters, selectedLocation, onSearchLocationSelect} = props;
  return (
    <DropdownBoxCoordinator
      shouldDisablePadding
      noInputMarginLeft
      value={
        <StyledFlexDiv>
          <FilterIconDiv>
            {searchFilters.localSearchFilterIcon
              ? renderFilterIcon(selectedLocation, searchFilters.localSearchFilterIcon)
              : ''}
          </FilterIconDiv>
          {renderFilterName(selectedLocation, searchFilters.localSearchFilterName)}
        </StyledFlexDiv>
      }
      renderDropdown={(popoverProps: PopoverTransferProps) => (
        <InboxesSearchLocationDropdown
          popoverProps={popoverProps}
          searchFilters={searchFilters}
          selectedFilter={selectedLocation}
          onSearchFilterSelect={onSearchLocationSelect}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

const extraFiltersModifiers = [
  ModifiersEnum.TAG,
  ModifiersEnum.ASSIGNEE,
  ModifiersEnum.MENTION,
  ModifiersEnum.WITH,
  ModifiersEnum.AUTHOR,
  ModifiersEnum.PARTICIPANT,
  ModifiersEnum.ACCOUNT,
  ModifiersEnum.CHANNEL_TYPE,
  ModifiersEnum.CUSTOM_FIELD,
  ModifiersEnum.OBJECT,
] as const;

function countExtraFiltersWithValues(builtQuery: BuiltQuery) {
  return extraFiltersModifiers.filter((modifier) => builtQuery.hasAnyModifierValue(modifier)).length;
}
